import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
} from 'generated/graphql';
import { useState } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';
import { AddOrEditIntegrationDrawerFooter } from '../../AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '../../AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '../../IntegrationDescriptionForm';

export const AppstoreFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
  integrationID,
}: GetAddIntegrationFormDrawerContentProps) => {
  const [appURL, setAppURL] = useState('');
  const [privateAPIKey, setPrivateAPIKey] = useState<string | undefined>();
  const [descriptionInput, setDescriptionInput] = useState('');
  const [issuerID, setIssuerID] = useState<string | undefined>();
  const [privateAPIKeyID, setPrivateAPIKeyID] = useState<string | undefined>();

  const { showErrorNotification } = useNotificationContext();

  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { status: createIntegrationStatus, createIntegration } =
    useCreateIntegrationEdgeServer({
      tenantID,
      direction: IntegrationDirection.Inbound,
      channel: IntegrationChannelType.Appstore,
      integrationType,
      createdByUser: userID,
      inputData: {
        appstore: {
          appURL,
          privateAPIKey,
          issuerID,
          privateAPIKeyID,
        },
      },
      description: descriptionInput.trim(),
      onError: (error: Error) => {
        if (isErrorCode(error, ErrorCode.DuplicateRecord)) {
          showErrorNotification({
            contentComponent: 'The app is already connected',
            props: {},
          });
          return;
        }
        showErrorNotification({
          contentComponent: `Something went wrong${
            error.message ? `: ${error.message}` : '!'
          }`,
          props: {},
        });
      },
      onCompleted: () => {
        onDrawerClose();
        trackIntegrationEvent({
          eventName: 'Integration Added',
          integrationChannelType: IntegrationChannelType.Appstore,
        });
      },
    });

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      action: 'reauth',
      channelType: integrationChannel.type,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
      },
    });

  const isReauthorizeForm = !!integrationID;

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    shouldValidateDescription: !isReauthorizeForm,
    channelType: IntegrationChannelType.Appstore,
    integrationType,
    hasValidFormFields: !!appURL,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      if (isReauthorizeForm) {
        await updateIntegration({
          integrationID,
          isActive: true,
          updateAuthData: {
            appstore: {
              appURL,
              privateAPIKey,
              issuerID,
              privateAPIKeyID,
            },
          },
        });
      } else {
        await createIntegration();
        trackIntegrationEvent({
          eventName: 'Integration Form Connect CTA Clicked',
          integrationChannelType: IntegrationChannelType.Appstore,
        });
      }
    },
  });

  const handleAppURLChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAppURL(event.target.value);
  };

  const handlePrivateAPIKeyChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPrivateAPIKey(event.target.value);
  };

  const loading =
    IsLoading(createIntegrationStatus) || IsLoading(updateIntegrationStatus);

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
          isReauthorizeForm={isReauthorizeForm}
        />
        <Stack p={3} spacing={4}>
          <Stack spacing={2}>
            <TextField
              required
              fullWidth
              autoComplete="off"
              label="App URL"
              name="appURL"
              placeholder="Enter App URL"
              value={appURL}
              disabled={loading}
              variant="outlined"
              onChange={handleAppURLChange}
            />
            <TextField
              fullWidth
              autoComplete="new-password"
              type="password"
              label="Private API Key (Optional)"
              multiline
              maxRows={4}
              name="privateAPIKey"
              placeholder="Enter Private API Key"
              value={privateAPIKey}
              disabled={loading}
              variant="outlined"
              onChange={handlePrivateAPIKeyChange}
              helperText="Adding API Key allows Enterpret to fetch detailed metadata associated with each review."
            />
            <TextField
              fullWidth
              autoComplete="new-password"
              type="password"
              label="Private API Key ID (Optional)"
              name="privateAPIKeyID"
              placeholder="Enter Private API Key ID"
              value={privateAPIKeyID}
              disabled={loading}
              variant="outlined"
              onChange={(event) => {
                setPrivateAPIKeyID(event.target.value);
              }}
            />
            <TextField
              fullWidth
              type="text"
              label="Issuer ID (Optional)"
              name="issuerID"
              placeholder="Enter Issuer ID"
              value={issuerID}
              disabled={loading}
              variant="outlined"
              onChange={(event) => {
                setIssuerID(event.target.value);
              }}
            />
          </Stack>
          {!isReauthorizeForm && (
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isPreValidatedDescription={isPreValidatedDescription}
              integrationType={integrationType}
            />
          )}
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        buttonText={isReauthorizeForm ? 'Reauthorize' : 'Integrate'}
        isDisabled={isDisabled}
        isLoading={loading}
      />
    </Stack>
  );
};
